import { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useToast } from '../../../components/ui/use-toast';
import { supabase } from '../../../lib/supabase';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Loader2, Check, X, Eye, Filter } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { useCheckPermissions } from '../../../middleware/checkPermissions';

interface Contestant {
  id: string;
  name: string;
  description: string;
  unique_code: string;
  photo_url: string;
  status: string;
  created_at: string;
  contest_id: string;
  contest: {
    id: string;
    name: string;
    code_unique: string;
  };
}

const PendingContestants = () => {
  const [contestants, setContestants] = useState<Contestant[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedContestants, setSelectedContestants] = useState<string[]>([]);
  const [viewContestant, setViewContestant] = useState<Contestant | null>(null);
  const [contests, setContests] = useState<{ id: string; name: string }[]>([]);
  const [selectedContest, setSelectedContest] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useCheckPermissions();

  useEffect(() => {
    if (user) {
      fetchContests();
      fetchContestants();
    }
  }, [user, selectedContest]);

  const fetchContests = async () => {
    try {
      const { data, error } = await supabase
        .from('contests')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setContests(data || []);
    } catch (error: any) {
      console.error('Erreur lors de la récupération des concours:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de charger les concours',
      });
    }
  };

  const fetchContestants = async () => {
    setLoading(true);
    try {
      console.log('Récupération des candidatures en attente...');
      
      // Récupérer uniquement les candidatures avec le statut "pending"
      let query = supabase
        .from('public_contestants_submissions')
        .select('*, contest:contests(id, name, code_unique)')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (selectedContest) {
        query = query.eq('contest_id', selectedContest);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erreur de requête:', error);
        throw error;
      }
      
      console.log('Candidatures récupérées:', data);
      setContestants(data as Contestant[]);
    } catch (error: any) {
      console.error('Erreur lors de la récupération des candidats:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de charger les candidats en attente',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedContestants(contestants.map(c => c.id));
    } else {
      setSelectedContestants([]);
    }
  };

  const handleSelectContestant = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedContestants(prev => [...prev, id]);
    } else {
      setSelectedContestants(prev => prev.filter(cId => cId !== id));
    }
  };

  const handleApproveSelected = async () => {
    if (selectedContestants.length === 0) return;

    setProcessing(true);
    try {
      // Récupérer les données des candidats sélectionnés
      const { data: selectedData, error: fetchError } = await supabase
        .from('public_contestants_submissions')
        .select('*')
        .in('id', selectedContestants);

      if (fetchError) throw fetchError;
      
      if (selectedData && selectedData.length > 0) {
        // Préparer les données pour l'insertion dans la table contestants
        const contestantsToInsert = selectedData.map(candidate => ({
          name: candidate.name,
          description: candidate.description,
          unique_code: candidate.unique_code,
          contest_id: candidate.contest_id,
          photo_url: candidate.photo_url,
          status: 'active', // Approuvé
          created_at: new Date().toISOString()
        }));

        // Insérer dans la table contestants
        const { error: insertError } = await supabase
          .from('contestants')
          .insert(contestantsToInsert);

        if (insertError) throw insertError;

        // Marquer comme traité dans la table des soumissions
        const { error: updateError } = await supabase
          .from('public_contestants_submissions')
          .update({ status: 'approved' })
          .in('id', selectedContestants);

        if (updateError) throw updateError;

        toast({
          title: 'Succès',
          description: `${selectedContestants.length} candidat(s) approuvé(s)`,
        });

        // Refresh the list
        fetchContestants();
        setSelectedContestants([]);
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'approbation des candidats:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible d\'approuver les candidats sélectionnés',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectSelected = async () => {
    if (selectedContestants.length === 0) return;

    setProcessing(true);
    try {
      const { error } = await supabase
        .from('public_contestants_submissions')
        .update({ status: 'rejected' })
        .in('id', selectedContestants);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: `${selectedContestants.length} candidat(s) rejeté(s)`,
      });

      // Refresh the list
      fetchContestants();
      setSelectedContestants([]);
    } catch (error: any) {
      console.error('Erreur lors du rejet des candidats:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de rejeter les candidats sélectionnés',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveContestant = async (id: string) => {
    setProcessing(true);
    try {
      // Récupérer les données du candidat
      const { data, error: fetchError } = await supabase
        .from('public_contestants_submissions')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      
      if (data) {
        // Insérer dans la table contestants
        const { error: insertError } = await supabase
          .from('contestants')
          .insert({
            name: data.name,
            description: data.description,
            unique_code: data.unique_code,
            contest_id: data.contest_id,
            photo_url: data.photo_url,
            created_at: new Date().toISOString()
          });

        if (insertError) throw insertError;

        // Marquer comme approuvé dans la table des soumissions
        const { error: updateError } = await supabase
          .from('public_contestants_submissions')
          .update({ status: 'approved' })
          .eq('id', id);

        if (updateError) throw updateError;

        toast({
          title: 'Succès',
          description: 'Candidat approuvé',
        });

        // Refresh the list
        fetchContestants();
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'approbation du candidat:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible d\'approuver le candidat',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectContestant = async (id: string) => {
    setProcessing(true);
    try {
      const { error } = await supabase
        .from('public_contestants_submissions')
        .update({ status: 'rejected' })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Candidat rejeté',
      });

      // Refresh the list
      fetchContestants();
    } catch (error: any) {
      console.error('Erreur lors du rejet du candidat:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de rejeter le candidat',
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <AdminLayout>
      <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                Candidatures en attente
              </span>
            </h1>
            <p className="text-gray-400 mt-1">
              Gérez les candidatures soumises par les participants
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="bg-white/5 border-white/10 text-white">
                  <Filter className="h-4 w-4 mr-2" />
                  Filtrer par concours
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-gray-900 border border-white/10 text-white">
                <DropdownMenuItem 
                  onClick={() => setSelectedContest(null)}
                  className={!selectedContest ? "bg-white/10" : ""}
                >
                  Tous les concours
                </DropdownMenuItem>
                {contests.map(contest => (
                  <DropdownMenuItem 
                    key={contest.id}
                    onClick={() => setSelectedContest(contest.id)}
                    className={selectedContest === contest.id ? "bg-white/10" : ""}
                  >
                    {contest.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex gap-2">
              <Button
                onClick={handleApproveSelected}
                disabled={selectedContestants.length === 0 || processing}
                className="bg-green-600 hover:bg-green-500 text-white"
              >
                <Check className="h-4 w-4 mr-2" />
                Approuver ({selectedContestants.length})
              </Button>
              <Button
                onClick={handleRejectSelected}
                disabled={selectedContestants.length === 0 || processing}
                variant="destructive"
              >
                <X className="h-4 w-4 mr-2" />
                Rejeter ({selectedContestants.length})
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
          </div>
        ) : contestants.length === 0 ? (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <Check className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-white mb-2">Aucune candidature en attente</h3>
            <p className="text-gray-400">
              Toutes les candidatures ont été traitées. Revenez plus tard pour voir les nouvelles soumissions.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center">
                          <Checkbox 
                            checked={selectedContestants.length === contestants.length}
                            onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
                            className="data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Photo</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Nom</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Concours</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Date de soumission</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contestants.map((contestant) => (
                      <tr key={contestant.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3">
                          <Checkbox 
                            checked={selectedContestants.includes(contestant.id)}
                            onCheckedChange={(checked) => handleSelectContestant(contestant.id, checked as boolean)}
                            className="data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-12 w-12 rounded-md overflow-hidden">
                            <img 
                              src={contestant.photo_url} 
                              alt={contestant.name} 
                              className="h-full w-full object-cover"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-white">{contestant.name}</div>
                            <div className="text-xs text-gray-400 font-mono">{contestant.unique_code}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-300">{contestant.contest.name}</td>
                        <td className="px-4 py-3 text-gray-300">{formatDate(contestant.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-white/5 border-white/10 text-white h-8 px-2"
                              onClick={() => setViewContestant(contestant)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-500 text-white h-8 px-2"
                              onClick={() => handleApproveContestant(contestant.id)}
                              disabled={processing}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="h-8 px-2"
                              onClick={() => handleRejectContestant(contestant.id)}
                              disabled={processing}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dialog de visualisation des détails du candidat */}
      <Dialog open={!!viewContestant} onOpenChange={(open) => !open && setViewContestant(null)}>
        <DialogContent className="bg-gray-900 border border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
              Détails du candidat
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Informations complètes sur la candidature
            </DialogDescription>
          </DialogHeader>

          {viewContestant && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
              <div className="md:col-span-1">
                <div className="rounded-xl overflow-hidden">
                  <img 
                    src={viewContestant.photo_url} 
                    alt={viewContestant.name} 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
              <div className="md:col-span-2 space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-white">{viewContestant.name}</h3>
                  <p className="text-sm text-gray-400 font-mono">{viewContestant.unique_code}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-1">Concours</h4>
                  <p className="text-white">{viewContestant.contest.name}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-1">Date de soumission</h4>
                  <p className="text-white">{formatDate(viewContestant.created_at)}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-1">Description</h4>
                  <div className="bg-white/5 rounded-xl p-4 text-gray-200">
                    {viewContestant.description}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-6 gap-2">
            <Button
              variant="outline"
              onClick={() => setViewContestant(null)}
              className="bg-white/5 border-white/10 text-white"
            >
              Fermer
            </Button>
            {viewContestant && (
              <>
                <Button
                  className="bg-green-600 hover:bg-green-500 text-white"
                  onClick={() => {
                    handleApproveContestant(viewContestant.id);
                    setViewContestant(null);
                  }}
                  disabled={processing}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approuver
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    handleRejectContestant(viewContestant.id);
                    setViewContestant(null);
                  }}
                  disabled={processing}
                >
                  <X className="h-4 w-4 mr-2" />
                  Rejeter
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PendingContestants;
